import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import ManagementHeaderTabs from '../Management/ManagementHeaderTabs';
import InventoryProgressionTab from './InventoryProgressionTab';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useQuery } from '@tanstack/react-query';
import { getInventoryDailyTasks, getInventoryOnboardingMap } from '../../services/api/implementation/impl';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Loader from '../../../shared/components/ui/Loader';
import DashboardFilters from '../../../shared/components/ui/DashboardFilters';
import InventoryGraphs from './InventoryGraphs';
import AiPulsePanel from '../../components/ai/AiPulsePanel';
import { navigateForInventoryTaskType } from '../../utils/inventoryTaskNavigation';
import { isDominoTask } from '../../utils/dailyTaskTypes';
import {
  buildInventoryHeaderTabParams,
  getInventoryHeaderTab
} from './inventoryDashboardUrlParams';
import { isDominoEnabled } from '../../../shared/utils/authStorage';
import { useSmartBack, shouldShowModuleHomeBack } from '../../../shared/navigation';

const InventoryDashBoard = () => {
  const { collapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userVertical = Cookies.get('userVertical');
  const isManagementVertical = userVertical?.toLowerCase() === 'management';
  const showProgressionTab = !isManagementVertical && isDominoEnabled();
  const headerTab = showProgressionTab ? getInventoryHeaderTab(searchParams) : 'dashboard';
  const isDashboardTab = headerTab === 'dashboard';
  const userID = Number(Cookies.get('userId'));
  const { onBack } = useSmartBack({ fallback: '/builder/dashboard' });
  const showHomeBack = shouldShowModuleHomeBack('inventory', location);
  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem('engineerSelectedDate') || new Date().toISOString().split('T')[0]
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [filters, setFilters] = useState({
    projectID: location.state?.projectID || null,
    month: location.state?.month || null
  });
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const hasProjectSelected =
    filters.projectID !== null &&
    filters.projectID !== undefined &&
    String(filters.projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(filters.projectID) : null;

  const { data: onboardingResponse } = useQuery({
    queryKey: ['inventoryOnboardingMap', resolvedProjectId],
    queryFn: () => getInventoryOnboardingMap(resolvedProjectId),
    enabled: showProgressionTab && !!resolvedProjectId
  });

  const progressionPendingCount = useMemo(() => {
    const progress = onboardingResponse?.data?.progress;
    if (progress?.total != null && progress?.completed != null) {
      return Math.max(0, progress.total - progress.completed);
    }

    return (onboardingResponse?.data?.steps || []).filter(
      (step) => step.status !== 'completed'
    ).length;
  }, [onboardingResponse]);

  const handleHeaderTabChange = useCallback(
    (tab) => {
      setSearchParams(buildInventoryHeaderTabParams(searchParams, tab), { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!showProgressionTab && getInventoryHeaderTab(searchParams) === 'progression') {
      setSearchParams(buildInventoryHeaderTabParams(searchParams, 'dashboard'), { replace: true });
    }
  }, [showProgressionTab, searchParams, setSearchParams]);

  useEffect(() => {
    // Track page view
    mixpanel.track('Inventory Dashboard Viewed', {
      user_id: userID,
      timestamp: new Date().toISOString()
    });
  }, [userID]);

  const { data: priorities, isLoading: isLoadingPriorities } = useQuery({
    queryKey: ['priorities', filters.projectID, selectedDate],
    queryFn: () => getInventoryDailyTasks(filters.projectID, selectedDate),
    enabled: isDashboardTab && !!filters.projectID && !!selectedDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userID,
        project_id: filters.projectID,
        follow_ups_count: data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // Only save to localStorage when user manually changes the date
    localStorage.setItem('engineerSelectedDate', date);
    setShowDatePicker(false);

    // Track date change
    mixpanel.track('Dashboard Date Changed', {
      user_id: userID,
      selected_date: date,
      project_id: filters.projectID,
      timestamp: new Date().toISOString()
    });
  };

  const handleFiltersChange = ({ projectID, month }) => {
    setFilters({ projectID, month });
  };

  const handleFiltersLoadingChange = (isLoading) => {
    setIsFiltersLoading(isLoading);
  };

  // Close date picker when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const getTaskSummary = (data) => {
    if (!data || !Array.isArray(data)) return [];

    const pendingTasks = data.filter((item) => {
      if (item.status === 'Completed') return false;
      return !isDominoTask(item);
    });

    const summary = pendingTasks.reduce((acc, task) => {
      const type = task.type || 'Unknown';
      if (!acc[type]) {
        acc[type] = {
          count: 0,
          tasks: []
        };
      }
      acc[type].count += 1;
      acc[type].tasks.push(task);
      return acc;
    }, {});

    return Object.entries(summary).map(([type, group]) => ({
      type,
      count: group.count,
      tasks: group.tasks
    }));
  };

  const prioritySummaries = useMemo(() => getTaskSummary(priorities), [priorities]);

  const handleTaskGroupClick = (summary) => {
    if (
      navigateForInventoryTaskType(navigate, summary.type, {
        projectId: filters.projectID
      })
    ) {
      return;
    }
    navigate(`/builder/inventory/dailytasks`, {
      state: {
        type: summary.type,
        projectID: filters.projectID,
        date: selectedDate
      }
    });
  };

  const renderTaskSummaryList = (summaries, emptyMessage) => {
    if (!summaries.length) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-2">📋</div>
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {summaries.map((summary) => (
          <div
            key={summary.type}
            className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
            onClick={() => handleTaskGroupClick(summary)}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-start gap-2">
                <span className="text-sm font-medium text-gray-800">{summary.type}</span>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-semibold text-gray-900">{summary.count}</span>
                  <span className="text-xs text-gray-500">tasks</span>
                </div>
              </div>
              <div className="text-xs text-gray-600 pl-2">
                {summary.tasks
                  .slice(0, 2)
                  .map((task) => task.name)
                  .join(', ')}
                {summary.tasks.length > 2 && ` +${summary.tasks.length - 2} more`}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <Loader
        isVisible={
          isFiltersLoading ||
          (isDashboardTab && isLoadingPriorities)
        }
      />

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
          }`}>
        <Header
          data={{ name: `Inventory Dashboard` }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
          showProjectSelector={false}>
          {showProgressionTab && (
            <ManagementHeaderTabs
              activeTab={headerTab}
              onTabChange={handleHeaderTabChange}
              tabCounts={hasProjectSelected ? { progression: progressionPendingCount } : undefined}
            />
          )}
        </Header>
        {isDashboardTab && (
          <DashboardFilters
            onFiltersChange={handleFiltersChange}
            onLoadingChange={handleFiltersLoadingChange}
            top="4rem"
            initialProjectID={location.state?.projectID}
            initialMonth={location.state?.month}
            rightContent={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    navigate('/builder/inventory/site-receipts', {
                      state: { projectID: filters.projectID, month: filters.month }
                    })
                  }
                  className="h-10 rounded-lg border border-[#2A565E] px-4 text-sm font-semibold text-[#2A565E]">
                  Site receipts
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate('/builder/inventory/pipeline', {
                      state: { projectID: filters.projectID, month: filters.month }
                    })
                  }
                  className="h-10 rounded-lg bg-[#2A565E] px-4 text-sm font-semibold text-white">
                  Open pipeline
                </button>
              </div>
            }
          />
        )}
      </div>

      {showProgressionTab && headerTab === 'progression' && (
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <InventoryProgressionTab
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onFiltersLoadingChange={handleFiltersLoadingChange}
          />
        </div>
      )}

      {/* Body Content */}
      {isDashboardTab && (
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="pt-[10rem] flex justify-flex-start items-start relative p-2 gap-7 pl-5">
          <div
            className={`flex flex-col gap-5 transition-all duration-300 ease-in-out ${collapsed ? 'w-[63%]' : 'w-[59%]'
              }`}>
            <AiPulsePanel projectId={filters.projectID} defaultVertical="inventory" />
            <InventoryGraphs projectID={filters.projectID} month={filters.month} />
          </div>

          {/* Action Center */}
          <div className="h-[75vh] overflow-hidden shadow flex flex-col bg-gray-100 w-[31%] fixed right-4">
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white p-4 shadow-lg">
              <div className="shrink-0">
                <div className="text-center font-medium text-sm flex gap-3 justify-center items-center mb-4">
                  <div className="relative date-picker-container">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="px-3 py-2 focus:outline-none text-sm font-medium text-[#346C75] bg-white transition-colors"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-[#1E4A53] mb-1">Action Center</h3>
                <p className="text-xs text-gray-500 mb-4">Daily priorities for the selected date.</p>
              </div>

              <div className="flex-1 overflow-y-auto pr-1">
                {renderTaskSummaryList(prioritySummaries, 'No priorities pending')}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </Layout>
  );
};

export default InventoryDashBoard;
